import { useWorkPeriodEventsQuery } from '../queries/work-period-events'
import { ALL_ABSENCES_SENTINEL, ALL_PRESENCES_SENTINEL } from '../components/filters/fields/events'

/**
 * Normalized events filter result
 * Contains the processed events array and optional excluded events array
 */
export interface NormalizedEventsFilters {
  events?: string[]
  'events!'?: string[]
}

/**
 * Hook to normalize event filters by transforming sentinel values into actual event IDs
 *
 * This hook handles the conversion of synthetic filter options:
 * - ALL_ABSENCES_SENTINEL: Expands to all absence event IDs
 * - ALL_PRESENCES_SENTINEL: Adds all absence event IDs to the exclusion list
 *
 * @param events - Optional array of event IDs or sentinel values to normalize
 * @returns Object containing normalized events and excluded events (if any)
 */
export const useNormalizeEventsFilters = (events?: string[]): NormalizedEventsFilters => {
  // Fetch all absence events from the API
  const workperiodEventsQuery = useWorkPeriodEventsQuery(
    { indicatesAbsence: true },
    { staleTime: 1000 * 60 * 5 },
  )

  // Initialize result object
  const result: NormalizedEventsFilters = {}

  // If no events provided, return empty result
  if (!events || events.length === 0) {
    return result
  }

  // Create a Set from the events array for efficient lookups
  const eventsSet = new Set(events)
  const excludedEvents: string[] = []
  const allAbsences = workperiodEventsQuery.data || []

  // Check if sentinel values are present
  const hasAllAbsencesOption = eventsSet.has(ALL_ABSENCES_SENTINEL)
  const hasAllPresencesOption = eventsSet.has(ALL_PRESENCES_SENTINEL)

  // If ALL_PRESENCES_SENTINEL is present, exclude all absence events
  if (hasAllPresencesOption) {
    excludedEvents.push(...allAbsences.map(absence => absence._id))
  }

  // If ALL_ABSENCES_SENTINEL is present, add all absence event IDs to the events set
  if (hasAllAbsencesOption) {
    allAbsences.forEach(absence => eventsSet.add(absence._id))
  }

  // Add excluded events to result if any
  if (excludedEvents.length > 0) {
    result['events!'] = excludedEvents
  }

  // Filter out sentinel values and non-string values, then add to result
  if (eventsSet.size > 0) {
    result.events = Array.from(eventsSet).filter(
      (event: unknown) =>
        event !== ALL_ABSENCES_SENTINEL &&
        event !== ALL_PRESENCES_SENTINEL &&
        typeof event === 'string',
    )
  }

  return result
}
